import { useState, useEffect, useCallback } from "react";
import { attendanceAPI } from '../../api/attendanceAPI';
import UserPcode from '../../components/userPcode';
import EmployeeSelector from '../../components/employeeSelector';
import Pagination from '../../components/pagination/pagination';
import { toast } from 'react-toastify';
import Spinner from "../../components/spinner/spinner";
import useUserName from '../../common/hooks/useUserName';
import './employeeAttendance.css';

export default function EmployeeAttendance() {
    const { userName } = useUserName();

    const [isProcessing, setIsProcessing] = useState(false);
    const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
    const [isLoadingEmployee, setIsLoadingEmployee] = useState(false);

    const [selectedPcode, setSelectedPcode] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loggedInEmployee, setLoggedInEmployee] = useState(null);
    const [fromDate, setFromDate] = useState("")
    const [toDate, setToDate] = useState("")
    const [isAdmin, setIsAdmin] = useState(false);
    
    const [attendanceData, setAttendanceData] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        totalCount: 0,
        totalPages: 0
    });

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const loadLoggedInEmployee = useCallback(async () => {
        if (!selectedPcode) return;

        setIsLoadingEmployee(true);
        try {
            const employees = await attendanceAPI.getEmployees(selectedPcode);
            const currentEmployee = employees.find(emp => emp.employeeCode === userName);

            if (currentEmployee) {
                setLoggedInEmployee(currentEmployee);
            }
        } catch (error) {
            console.error('Error loading logged in employee:', error);
        } finally {
            setIsLoadingEmployee(false);
        }
    }, [selectedPcode, userName]);

    const checkAdminAccess = useCallback(async () => {
        setIsCheckingAdmin(true);
        try {
            const result = await attendanceAPI.checkAdminAccess(selectedPcode);
            setIsAdmin(result.isAdmin);
        } catch (error) {
            console.error('Error checking admin access:', error);
            setIsAdmin(false);
        } finally {
            setIsCheckingAdmin(false);
        }
    }, [selectedPcode]);

    // Check admin access when pcode changes
    useEffect(() => {
        if (selectedPcode && selectedPcode.trim() !== '') {
            checkAdminAccess();
            loadLoggedInEmployee();
        }
    }, [selectedPcode, checkAdminAccess, loadLoggedInEmployee]);

    // Set logged-in employee as selected when user is not admin
    useEffect(() => {
        if (!isAdmin && loggedInEmployee && !isCheckingAdmin) {
            setSelectedEmployee(loggedInEmployee);
        }
    }, [isAdmin, loggedInEmployee, isCheckingAdmin]);

    const handlePreview = () => {
        if (fromDate.trim().length === 0) {
            toast.warn('Please select a Date From');
            return;
        }

        if (toDate.trim().length === 0) {
            toast.warn('Please select a Date To');
            return;
        }

        if (new Date(fromDate) > new Date(toDate)) {
            toast.warn('Invalid Date Range');
            return;
        }

        if (!selectedEmployee) {
            toast.warn('Please select an employee');
            return;
        }

        setCurrentPage(1);
        setIsProcessing(true);
        loadAttendanceDetails(1);
    };

    const loadAttendanceDetails = (page = currentPage) => {
        const isShowAll = selectedEmployee && selectedEmployee.employeeCode === 'ALL';
        const empCode = isShowAll ? null : (selectedEmployee ? selectedEmployee.employeeCode : null);

        attendanceAPI.getAttendanceDetails(fromDate, toDate, selectedPcode, empCode, isShowAll, page, pageSize)
            .then((response) => {
                setAttendanceData(response.data || []);
                setPagination(response.pagination || {
                    currentPage: page,
                    pageSize: pageSize,
                    totalCount: 0,
                    totalPages: 0
                });
                setCurrentPage(page);
            })
            .catch((error) => {
                console.error('Error loading attendance details:', error);
                toast.error('Failed to load attendance details');
            })
            .finally(() => setIsProcessing(false));
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.totalPages) {
            setIsProcessing(true);
            loadAttendanceDetails(page);
        }
    };

    const onChangePCode = (pcode) => {
        setSelectedPcode(pcode);
        setSelectedEmployee(null);
        setLoggedInEmployee(null);
        setAttendanceData([]);        
        setCurrentPage(1);
    };

    const onChangeEmployee = (employee) => {
        setSelectedEmployee(employee);
        setAttendanceData([]);      
        setCurrentPage(1);        
    };

    const getStatusBadgeClass = (status) => {       
        switch (status) {
            case 'Working Day': return 'badge bg-success';
            case 'Absent': return 'badge bg-danger';
            case 'Short Leave': return 'badge bg-warning';
            case 'Half Day': return 'badge bg-info';
            default: return 'badge bg-secondary';
        }
    }

    return (
        <>
            <div className="row">
                <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12 p-2 rounded contentLeveApproval form__body employee-attendance-container">
                    <div className="row mt-1">
                        <div className="col-2 col-sm-2 col-md-4 col-lg-4 col-xl-4 col-xxl-4 text-start"></div>
                        <div className="col-8 col-sm-8 col-md-4 col-lg-4 col-xl-4 col-xxl-4 text-center">
                            <h4>Employee Attendance</h4>
                        </div>
                        <div className="col-2 col-sm-2 col-md-4 col-lg-4 col-xl-4 col-xxl-4 text-center"></div>
                    </div>

                    <div className="row">
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                        <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
                            <div className="input-group input-group-sm">
                                <UserPcode setSelectedPcode={setSelectedPcode} triggerOnChangePCode={onChangePCode} />
                            </div>
                        </div>
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                    </div>

                    <div className="row">
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                        <div className="col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6 col-xxl-6">
                            <div className="input-group input-group-sm">
                                <EmployeeSelector
                                    selectedPcode={selectedPcode}
                                    setSelectedEmployee={setSelectedEmployee}
                                    disabled={!isAdmin || isCheckingAdmin || isLoadingEmployee}
                                    placeholder={
                                        isCheckingAdmin || isLoadingEmployee ? "Loading..." :
                                            !isAdmin ? loggedInEmployee ? `(${loggedInEmployee.employeeCode}) ${loggedInEmployee.employeeName}` : "Loading your details..." :
                                                "Select Employee"
                                    }
                                    triggerOnChangeEmployee={onChangeEmployee}
                                    selectedEmployee={selectedEmployee}
                                    showAllOption={isAdmin && !isCheckingAdmin}
                                />
                            </div>
                        </div>
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                    </div>

                    <div className="row">
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3 col-xxl-3 mb-2 mb-sm-0">
                            <div className="input-group input-group-sm">
                                <input
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    type="date"
                                    className="form-control"
                                    placeholder="From Date"
                                />
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3 col-xxl-3">
                            <div className="input-group input-group-sm">
                                <input
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    type="date"
                                    className="form-control"
                                    placeholder="To Date"
                                />
                            </div>
                        </div>
                        <div className="col-0 col-sm-0 col-md-3 col-lg-3 col-xl-3 col-xxl-3"></div>
                    </div>

                    <div className="row">
                        <div className="col-12 col-sm-12 col-md-12 col-lg-9 col-xl-9 col-xxl-9 text-end">
                            <div className="btn btn-sm">
                                <button onClick={handlePreview} type="button" className="btn btn-success mr-1">
                                    Preview
                                </button>                                
                            </div>
                        </div>
                        <div className='col-0 col-sm-0 col-md-3 col-md-3 col-lg-3 col-xl-3 col-xxl-3'></div>
                    </div>

                    <div className="row">
                        <div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 col-xxl-12 mt-2 table-responsive mt-0 pt-0'>
                            {
                                isProcessing === true ?
                                    <div className="text-center"><Spinner /></div>
                                    :
                                    <table className="table text-nowrap table-responsive-sm table-responsive-md table">
                                        <thead className="thead--color">
                                            <tr>
                                                {(selectedEmployee && selectedEmployee.employeeCode === 'ALL') || !isAdmin ? (
                                                    <>
                                                        <th className="d-none d-md-table-cell">Employee Code</th>
                                                        <th>Employee Name</th>
                                                    </>
                                                ) : (
                                                    <th></th>
                                                )}
                                                <th className="d-none d-md-table-cell">Date</th>
                                                <th>InTime</th>
                                                <th>OutTime</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                attendanceData.length > 0 ?
                                                    attendanceData.map((attendance, index) => (
                                                        <tr key={index}>
                                                            {(selectedEmployee && selectedEmployee.employeeCode === 'ALL') || !isAdmin ? (
                                                                <>
                                                                    <td className="d-none d-md-table-cell">{attendance.employeeCode}</td>
                                                                    <td>
                                                                        <div className="fw-bold">{attendance.employeeName}</div>
                                                                        <div className="d-md-none">
                                                                            <small className="text-muted">({attendance.employeeCode})</small>
                                                                        </div>
                                                                        <div className="d-md-none">
                                                                            <small className="text-muted">{attendance.startDate}</small>
                                                                        </div>
                                                                    </td>
                                                                </>
                                                            ) : (
                                                                <td>
                                                                    <div className="d-md-none">
                                                                        <small className="text-muted">{attendance.startDate}</small>
                                                                    </div>
                                                                </td>
                                                            )}
                                                            <td className="d-none d-md-table-cell">{attendance.startDate}</td>
                                                            <td>{attendance.inTime || '-'}</td>
                                                            <td>{attendance.outTime || '-'}</td>
                                                            <td>
                                                                <span className={getStatusBadgeClass(attendance.status)}>
                                                                    {attendance.status}
                                                                </span>
                                                                {attendance.workingHours && attendance.workingHours !== '00:00' && (
                                                                    <><br /><small className="text-muted">{attendance.workingHours} hrs</small></>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )) :
                                                    !isProcessing && (
                                                        <tr>
                                                            <td colSpan="6" className="text-center text-muted">
                                                                No attendance data found for the selected criteria
                                                            </td>
                                                        </tr>
                                                    )
                                            }
                                        </tbody>
                                    </table>
                            }
                        </div>
                    </div>

                    {/* Pagination */}
                    {attendanceData.length > 0 && pagination.totalPages > 1 && (
                        <div className="row">
                            <div className="col-12">
                                <Pagination
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    onPageChange={handlePageChange}
                                    pageSize={pagination.pageSize}
                                    totalRecords={pagination.totalCount}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
